import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField, Segmented } from '../components/Inputs'
import PaintWizard from '../components/PaintWizard'
import type { PaintWizardGoal } from '../components/PaintWizard'
import { computePaint, computePaintCoverage, DEFAULT_PAINT, planPaintCans } from '../calculators/paint'
import type { PaintInputs, Room, PaintCanPart } from '../calculators/paint'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { ROOM_PRESETS, CUSTOM_DIMS, CUSTOM_ROOM_KEY, toUnit, matchedPreset, dropdownValue, presetLabel } from '../lib/roomPresets'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/paint/'
const CATEGORY = 'paint-and-walls'

interface CalcContent {
  title: string
  desc: string
  h1: string
  intro: string
  showPaintIn: string
  gallons: string
  litres: string
  doorsTotal: string
  windowsTotal: string
  alsoCeilings: string
  includePrimer: string
  wasteTip: string
  coverageLabel: string
  coverageTip: string
  sqft: string
  assumptionsNote: string
  savePrefix: string
  primaryName: string
  primaryLabel: string
  secondaryName: string
  aboutH2: string
  aboutP1: string
  howH2: string
  howP1: string
  formula: string
  howP2: string
  mistakesH3: string
  mistakes: string[]
  relatedH2: string
  reviewer: string
  faqs: QA[]
}

const EN: CalcContent = {
  title: 'Paint Calculator — How Much Paint Do I Need? | RenoSheets',
  desc: 'Free paint calculator. Add one or more rooms and get the exact paint needed in gallons or litres, plus a complete materials list — primer, tape, rollers and more. No signup.',
  h1: 'Paint Calculator',
  intro: 'Add your rooms for the exact paint needed — in gallons or litres — plus the primer, tape, rollers and drop cloths you\'ll actually need.',
  showPaintIn: 'Show paint in',
  gallons: 'Gallons',
  litres: 'Litres',
  doorsTotal: 'Doors (total)',
  windowsTotal: 'Windows (total)',
  alsoCeilings: 'Also paint the ceilings',
  includePrimer: 'Include primer (bare / patched / colour change)',
  wasteTip: 'A little extra paint to cover spills, touch-ups, uneven or textured surfaces, and a margin so you don\'t run short. 10% is a sensible default; use 15–20% for textured walls or deep colours.',
  coverageLabel: 'Coverage per gallon',
  coverageTip: 'The area one gallon of paint covers in a single coat — typically 350–400 sq ft on a smooth, primed surface.',
  sqft: 'sq ft',
  assumptionsNote: 'These assumptions are yours to adjust — we never pad quantities.',
  savePrefix: 'Paint',
  primaryName: 'Interior paint',
  primaryLabel: 'of interior paint',
  secondaryName: 'Primer',
  aboutH2: 'What actually affects how much paint you need',
  aboutP1: 'Square footage is only the starting point. Wall texture changes how much paint gets absorbed — smooth, previously painted drywall takes less than bare drywall, brick, or stucco, which can soak up an extra coat. Color change matters just as much: going from a pale wall to a saturated or dark color (or the reverse) usually needs two full coats and sometimes a tinted primer, while touching up a similar shade can often get by with one. Sheen plays a small role too — flat and eggshell finishes spread slightly further per gallon than semi-gloss or high-gloss, which carry less pigment volume. This calculator starts from a standard 350–400 sq ft per gallon coverage rate and lets you adjust coats, primer and waste allowance so the number matches your walls, not a generic room.',
  howH2: 'How to calculate how much paint you need',
  howP1: 'Paint is estimated from the paintable area — the wall surface left after you remove doors and windows. For each room the calculator works out:',
  formula: 'paintable area = (room perimeter × wall height) − openings\ngallons = (paintable area × coats × (1 + waste%)) ÷ coverage',
  howP2: 'Add as many rooms as you like and the areas are summed. A standard interior door is treated as 21 sq ft and a window as 15 sq ft. One gallon covers about 350–400 sq ft per coat, so we use 350 by default and round up to the nearest quart — never a padded whole gallon.',
  mistakesH3: 'Common mistakes',
  mistakes: [
    'Forgetting the second coat — most colours need two for even coverage.',
    'Skipping primer on bare drywall or big colour changes.',
    'Buying paint for one room when the whole project needs more — add every room above.',
  ],
  relatedH2: 'Related calculators',
  reviewer: 'Reviewed by the RenoSheets team for calculation accuracy. Method: standard coverage rates (350–400 sq ft/gal) with a user-adjustable waste allowance, summed across rooms. Last updated 2026-05-26. Estimates only — verify before purchasing.',
  faqs: [
    { q: 'How much paint do I need for a 12x12 room?', a: 'A 12 × 12 ft room with 8 ft walls has about 384 sq ft of wall area. After subtracting a standard door and window (≈348 sq ft) and adding 10% for waste, two coats need about 2¼ gallons of paint — that is, 2 gallons plus a quart. Add about 1 gallon of primer if the walls are bare, patched, or changing colour.' },
    { q: 'How much does a gallon of paint cover?', a: 'One gallon of interior latex paint typically covers 350 to 400 sq ft in a single coat on a smooth, primed surface. Rough or porous surfaces cover less, so most projects plan on the lower end and apply two coats.' },
    { q: 'Can I calculate paint for a whole house or multiple rooms at once?', a: 'Yes — add each room with the “Add another room” button and the calculator totals the wall area across all of them, then gives you the combined paint and materials. You can switch the result between gallons and litres.' },
    { q: 'How much paint do I need for a ceiling?', a: 'Ceiling paint is based on the floor footprint: length × width. A 12 × 12 ft ceiling is 144 sq ft, so two coats need about 1 gallon. Tick “also paint the ceilings” to add every room’s ceiling automatically.' },
    { q: 'Is one coat of paint enough?', a: 'One coat can work when repainting a similar colour over a primed, sealed surface. For colour changes, bare drywall, or deep colours, plan on two coats for even coverage. Set the number of coats and the estimate updates instantly.' },
    { q: 'What is a waste allowance and what should I use?', a: 'A waste allowance is a little extra paint added to cover spills, touch-ups, uneven or textured surfaces, and a margin so you don’t run short mid-wall. 10% is a sensible default; bump it to 15–20% for textured walls, lots of cutting-in, or deep colours.' },
    { q: 'Do I need primer?', a: 'Use primer on new drywall, patched areas, stains, glossy finishes, or big colour changes. When repainting a similar colour over a sound wall, a paint-and-primer product in two coats is usually enough.' },
  ],
}

const FR: CalcContent = {
  title: 'Calculateur de peinture — Quelle quantité me faut-il? | RenoSheets',
  desc: 'Calculateur de peinture gratuit. Ajoutez une ou plusieurs pièces et obtenez la quantité exacte en gallons ou en litres, plus une liste complète de matériaux — apprêt, ruban, rouleaux et plus. Sans inscription.',
  h1: 'Calculateur de peinture',
  intro: 'Ajoutez vos pièces pour la quantité exacte de peinture — en gallons ou en litres — plus l’apprêt, le ruban, les rouleaux et les toiles de protection dont vous aurez vraiment besoin.',
  showPaintIn: 'Afficher la peinture en',
  gallons: 'Gallons',
  litres: 'Litres',
  doorsTotal: 'Portes (total)',
  windowsTotal: 'Fenêtres (total)',
  alsoCeilings: 'Peindre aussi les plafonds',
  includePrimer: 'Inclure l’apprêt (surface nue / réparée / changement de couleur)',
  wasteTip: 'Un peu de peinture en plus pour les éclaboussures, les retouches, les surfaces inégales ou texturées, et une marge pour ne pas en manquer. 10 % est un bon choix par défaut; utilisez 15–20 % pour les murs texturés ou les couleurs foncées.',
  coverageLabel: 'Couverture par gallon',
  coverageTip: 'La surface qu’un gallon de peinture couvre en une couche — habituellement 350–400 pi² sur une surface lisse et apprêtée.',
  sqft: 'pi²',
  assumptionsNote: 'Ces hypothèses sont à vous d’ajuster — nous ne gonflons jamais les quantités.',
  savePrefix: 'Peinture',
  primaryName: 'Peinture d’intérieur',
  primaryLabel: 'de peinture d’intérieur',
  secondaryName: 'Apprêt',
  aboutH2: 'Ce qui influence vraiment la quantité de peinture nécessaire',
  aboutP1: 'La superficie n’est qu’un point de départ. La texture du mur change la quantité de peinture absorbée — le gypse lisse déjà peint en absorbe moins que le gypse nu, la brique ou le stucco, qui peuvent exiger une couche de plus. Le changement de couleur compte tout autant : passer d’un mur pâle à une couleur saturée ou foncée (ou l’inverse) demande habituellement deux couches complètes et parfois un apprêt teinté, tandis qu’une retouche dans une teinte semblable peut souvent se contenter d’une seule couche. Le fini a aussi un léger effet — les finis mat et coquille d’œuf couvrent un peu plus par gallon que les finis semi-lustré ou lustré, qui contiennent moins de pigment. Ce calculateur part d’un taux de couverture standard de 350–400 pi² par gallon et vous permet d’ajuster les couches, l’apprêt et la marge de perte pour que le résultat corresponde à vos murs, pas à une pièce générique.',
  howH2: 'Comment calculer la quantité de peinture nécessaire',
  howP1: 'La peinture est estimée à partir de la surface à peindre — la surface des murs une fois les portes et fenêtres soustraites. Pour chaque pièce, le calculateur détermine :',
  formula: 'surface à peindre = (périmètre de la pièce × hauteur des murs) − ouvertures\ngallons = (surface à peindre × couches × (1 + % perte)) ÷ couverture',
  howP2: 'Ajoutez autant de pièces que vous voulez et les surfaces s’additionnent. Une porte intérieure standard compte pour 21 pi² et une fenêtre pour 15 pi². Un gallon couvre environ 350–400 pi² par couche; nous utilisons 350 par défaut et arrondissons au quart de gallon le plus proche — jamais un gallon entier gonflé.',
  mistakesH3: 'Erreurs fréquentes',
  mistakes: [
    'Oublier la deuxième couche — la plupart des couleurs en demandent deux pour une couverture uniforme.',
    'Sauter l’apprêt sur le gypse nu ou lors d’un grand changement de couleur.',
    'Acheter la peinture pour une seule pièce alors que tout le projet en demande plus — ajoutez chaque pièce ci-dessus.',
  ],
  relatedH2: 'Calculateurs connexes',
  reviewer: 'Révisé par l’équipe RenoSheets pour l’exactitude des calculs. Méthode : taux de couverture standards (350–400 pi²/gal) avec une marge de perte ajustable, additionnés sur toutes les pièces. Dernière mise à jour 2026-05-26. Estimations seulement — vérifiez avant d’acheter.',
  faqs: [
    { q: 'Combien de peinture me faut-il pour une pièce de 12 x 12?', a: 'Une pièce de 12 × 12 pi avec des murs de 8 pi a environ 384 pi² de surface murale. Après avoir soustrait une porte et une fenêtre standards (≈348 pi²) et ajouté 10 % de perte, deux couches demandent environ 2¼ gallons de peinture — soit 2 gallons plus un quart. Ajoutez environ 1 gallon d’apprêt si les murs sont nus, réparés ou changent de couleur.' },
    { q: 'Quelle surface couvre un gallon de peinture?', a: 'Un gallon de peinture au latex d’intérieur couvre habituellement 350 à 400 pi² en une couche sur une surface lisse et apprêtée. Les surfaces rugueuses ou poreuses couvrent moins, alors la plupart des projets prévoient le bas de la fourchette et appliquent deux couches.' },
    { q: 'Puis-je calculer la peinture pour toute une maison ou plusieurs pièces à la fois?', a: 'Oui — ajoutez chaque pièce avec le bouton « Ajouter une pièce » et le calculateur additionne la surface murale de toutes, puis donne la peinture et les matériaux combinés. Vous pouvez basculer le résultat entre gallons et litres.' },
    { q: 'Combien de peinture pour un plafond?', a: 'La peinture de plafond se base sur l’empreinte au sol : longueur × largeur. Un plafond de 12 × 12 pi fait 144 pi², donc deux couches demandent environ 1 gallon. Cochez « Peindre aussi les plafonds » pour ajouter automatiquement le plafond de chaque pièce.' },
    { q: 'Une seule couche de peinture suffit-elle?', a: 'Une couche peut suffire pour repeindre une couleur semblable sur une surface apprêtée et scellée. Pour un changement de couleur, du gypse nu ou des couleurs foncées, prévoyez deux couches pour une couverture uniforme. Réglez le nombre de couches et l’estimation se met à jour instantanément.' },
    { q: 'Qu’est-ce qu’une marge de perte et laquelle utiliser?', a: 'Une marge de perte est un peu de peinture en plus pour couvrir les éclaboussures, les retouches, les surfaces inégales ou texturées, et une marge pour ne pas en manquer en plein mur. 10 % est un bon défaut; montez à 15–20 % pour les murs texturés, beaucoup de coupes, ou des couleurs foncées.' },
    { q: 'Ai-je besoin d’apprêt?', a: 'Utilisez l’apprêt sur le gypse neuf, les zones réparées, les taches, les finis lustrés ou les grands changements de couleur. Pour repeindre une couleur semblable sur un mur en bon état, un produit peinture-et-apprêt en deux couches suffit habituellement.' },
  ],
}

export default function PaintCalculator() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<PaintInputs>(DEFAULT_PAINT)
  const [saved, setSaved] = useState(false)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [wizardGoal, setWizardGoal] = useState<PaintWizardGoal>('forward')
  const r = useMemo(() => computePaint(i, locale), [i, locale])
  // Reverse path: given the paint they have, how much area can they paint?
  const rev = useMemo(
    () => computePaintCoverage(i.gallonsHave, i.outputUnit, i.coveragePerGallon, i.coats),
    [i.gallonsHave, i.outputUnit, i.coveragePerGallon, i.coats],
  )
  const set = (patch: Partial<PaintInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }
  const setRoom = (idx: number, patch: Partial<Room>) =>
    set({ rooms: i.rooms.map((room, k) => (k === idx ? { ...room, ...patch } : room)) })

  const cvDim = (ft: number) => toUnit(ft, i.unit)
  const roomDefault = (idx: number) => (locale === 'fr' ? `Pièce ${idx + 1}` : `Room ${idx + 1}`)

  const applyPreset = (idx: number, label: string) => {
    // Switching to "Custom room" — only clear the name if the current name is a
    // preset (so we don't blow away a custom name they typed earlier). Dimensions
    // are kept as-is so the user can refine without re-entering.
    if (label === CUSTOM_ROOM_KEY) {
      // Switching to Custom — give the room a default name unless the user
      // already typed a custom one (i.e. current is non-empty AND isn't a
      // preset label). Empty current (the "Select a room" placeholder state)
      // also gets the default so the input appears for them to type.
      const current = i.rooms[idx].name
      if (!current || matchedPreset(current)) setRoom(idx, { name: roomDefault(idx) })
      return
    }
    const p = ROOM_PRESETS.find((rp) => rp.label === label)
    if (!p) return
    setRoom(idx, { name: presetLabel(p, locale), length: cvDim(p.lFt), width: cvDim(p.wFt), height: cvDim(p.hFt) })
  }

  // New rooms start with an empty name so the dropdown shows the "Select a
  // room" placeholder — user picks a preset or "Custom room" themselves.
  const addRoom = () =>
    set({ rooms: [...i.rooms, { name: '', length: cvDim(CUSTOM_DIMS.lFt), width: cvDim(CUSTOM_DIMS.wFt), height: cvDim(CUSTOM_DIMS.hFt) }] })
  const removeRoom = (idx: number) => set({ rooms: i.rooms.filter((_, k) => k !== idx) })

  const lin = i.unit === 'imperial' ? (locale === 'fr' ? 'pi' : 'ft') : 'm'

  const onSave = async () => {
    const roomNames = i.rooms.map((rm, k) => rm.name || roomDefault(k)).slice(0, 3).join(', ')
    await saveSheet({
      id: genId(),
      type: 'paint',
      name: `${c.savePrefix} — ${roomNames}`,
      createdAt: new Date().toISOString(),
      summary: `${r.paint.qty} ${r.paint.unit} · ${i.rooms.length}`,
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'paint')
  const coatsWord = (n: number) => (locale === 'fr' ? `${n} couche${n === 1 ? '' : 's'}` : `${n} coat${n === 1 ? '' : 's'}`)

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Built from the same numbers the math used so the user can verify every
  // assumption. JSX (not plain string) so we can highlight formulas + sub-totals.
  const sqftWord = locale === 'fr' ? 'pi²' : 'sq ft'
  const buildExplain = (): React.ReactNode => {
    if (i.inputMode === 'area') {
      const withWaste = Math.round(r.paintableArea * (1 + i.wastePct / 100))
      return (
        <ol className="list-decimal space-y-1 pl-5">
          <li>{locale === 'fr' ? 'Surface saisie' : 'Area you entered'}: <strong>{r.paintableArea} {sqftWord}</strong></li>
          <li>{locale === 'fr' ? `+ ${i.wastePct} % de perte` : `+ ${i.wastePct}% waste`}: <strong>{withWaste} {sqftWord}</strong></li>
          <li>{locale === 'fr' ? `× ${i.coats} couche${i.coats === 1 ? '' : 's'}` : `× ${i.coats} coat${i.coats === 1 ? '' : 's'}`}: <strong>{Math.round(withWaste * i.coats)} {sqftWord}</strong></li>
          <li>{locale === 'fr' ? `÷ ${i.coveragePerGallon} pi²/gal de couverture` : `÷ ${i.coveragePerGallon} ${sqftWord}/gal coverage`} = <strong>{((withWaste * i.coats) / i.coveragePerGallon).toFixed(2)} gal</strong></li>
          <li>{locale === 'fr' ? 'Arrondi au quart de gallon le plus proche' : 'Rounded up to nearest quart'} = <strong>{r.paint.qty} {r.paint.unit === 'litre' ? 'L' : r.paint.unit}</strong></li>
        </ol>
      )
    }
    // Dimensions mode — break down the wall-area math too
    const wallArea = i.rooms.reduce((s, rm) => s + 2 * (rm.length + rm.width) * rm.height, 0)
    const ceilingArea = i.includeCeiling ? i.rooms.reduce((s, rm) => s + rm.length * rm.width, 0) : 0
    const openings = i.doors * 21 + i.windows * 15
    const withWaste = Math.round(r.paintableArea * (1 + i.wastePct / 100))
    return (
      <ol className="list-decimal space-y-1 pl-5">
        <li>
          {locale === 'fr' ? 'Surface des murs' : 'Wall area'} ({i.rooms.length}{' '}
          {locale === 'fr' ? (i.rooms.length === 1 ? 'pièce' : 'pièces') : (i.rooms.length === 1 ? 'room' : 'rooms')}):
          {' '}<strong>{Math.round(wallArea)} {sqftWord}</strong>
        </li>
        {i.includeCeiling && (
          <li>{locale === 'fr' ? '+ Plafonds' : '+ Ceilings'}: <strong>{Math.round(ceilingArea)} {sqftWord}</strong></li>
        )}
        <li>
          {locale === 'fr'
            ? `− Ouvertures (${i.doors} porte${i.doors === 1 ? '' : 's'} × 21 + ${i.windows} fenêtre${i.windows === 1 ? '' : 's'} × 15)`
            : `− Openings (${i.doors} door${i.doors === 1 ? '' : 's'} × 21 + ${i.windows} window${i.windows === 1 ? '' : 's'} × 15)`}:
          {' '}<strong>−{openings} {sqftWord}</strong>
        </li>
        <li>= {locale === 'fr' ? 'Surface à peindre' : 'Paintable area'}: <strong>{r.paintableArea} {sqftWord}</strong></li>
        <li>{locale === 'fr' ? `+ ${i.wastePct} % de perte` : `+ ${i.wastePct}% waste`}: <strong>{withWaste} {sqftWord}</strong></li>
        <li>{locale === 'fr' ? `× ${i.coats} couche${i.coats === 1 ? '' : 's'}` : `× ${i.coats} coat${i.coats === 1 ? '' : 's'}`}: <strong>{Math.round(withWaste * i.coats)} {sqftWord}</strong></li>
        <li>{locale === 'fr' ? `÷ ${i.coveragePerGallon} pi²/gal de couverture` : `÷ ${i.coveragePerGallon} ${sqftWord}/gal coverage`} = <strong>{((withWaste * i.coats) / i.coveragePerGallon).toFixed(2)} gal</strong></li>
        <li>{locale === 'fr' ? 'Arrondi à la taille de contenant achetable' : 'Rounded up to a buyable can size'}: <strong>{r.paint.qty} {r.paint.unit === 'litre' ? 'L' : r.paint.unit}</strong></li>
      </ol>
    )
  }
  // In area mode the room-name list collapses to a single area entry.
  const acrossClause = i.inputMode === 'dimensions'
    ? (locale === 'fr' ? ` sur ${i.rooms.map((rm, k) => rm.name || roomDefault(k)).join(', ')}` : ` across ${i.rooms.map((rm, k) => rm.name || roomDefault(k)).join(', ')}`)
    : ''
  const assumptions = locale === 'fr'
    ? `Selon ${r.paintableArea} pi²${acrossClause} · ${coatsWord(i.coats)} · ${i.wastePct} % de perte · ${i.coveragePerGallon} pi²/gal`
    : `Based on ${r.paintableArea} sq ft${acrossClause} · ${coatsWord(i.coats)} · ${i.wastePct}% waste · ${i.coveragePerGallon} sq ft/gal`

  // The "How to calculate" formula + note follow the selected output unit — a
  // user who picked litres shouldn't be taught the math in gallons. Coverage is
  // rated per gallon, so the litre form converts with × 3.785 (L per gallon).
  const isLitreOut = i.outputUnit === 'litre'
  const formulaText = !isLitreOut
    ? c.formula
    : locale === 'fr'
      ? `surface à peindre = (périmètre de la pièce × hauteur des murs) − ouvertures
litres = (surface à peindre × couches × (1 + % perte)) ÷ couverture × 3,785`
      : `paintable area = (room perimeter × wall height) − openings
litres = (paintable area × coats × (1 + waste%)) ÷ coverage × 3.785`
  const howP2Text = !isLitreOut
    ? c.howP2
    : locale === 'fr'
      ? 'Ajoutez autant de pièces que vous voulez et les surfaces s’additionnent. Une porte intérieure standard compte pour 21 pi² et une fenêtre pour 15 pi². La couverture est indiquée par gallon (≈ 350–400 pi²); nous divisons par elle puis multiplions par 3,785 L/gallon pour obtenir les litres, et arrondissons à une taille de contenant achetable (p. ex. 4 L) — jamais une quantité gonflée.'
      : 'Add as many rooms as you like and the areas are summed. A standard interior door is treated as 21 sq ft and a window as 15 sq ft. Coverage is rated per gallon (≈ 350–400 sq ft), so we divide by it and multiply by 3.785 L/gallon to get litres, then round up to a buyable can size (e.g. a 4 L can) — never a padded amount.'

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={c.title} description={c.desc} path={PATH} schema={softwareAppSchema(calcName('paint', 'Paint Calculator', locale), c.desc, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Paint & Walls', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('paint', 'Paint Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-1 max-w-content text-text-muted">{c.intro}</p>

      {/* Entry banner for the guided quiz — only shown when wizard closed.
          Dark-green CTA with hover lift, arrow slide, and shadow boost. */}
      {!wizardOpen && (
        <button
          type="button"
          onClick={() => setWizardOpen(true)}
          className="group mt-4 flex w-full items-center justify-between rounded-lg bg-green-800 px-5 py-4 text-left text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-green-700 hover:shadow-xl sm:w-auto"
        >
          <span className="flex items-center gap-3">
            <span className="text-xl transition-transform duration-200 group-hover:rotate-12" aria-hidden>✨</span>
            <span>
              <span className="block text-lg font-semibold sm:text-xl">{locale === 'fr' ? 'Première fois? Essayez le quiz guidé' : 'First time? Try the guided quiz'}</span>
              <span className="block text-sm text-white/80">{locale === 'fr' ? '~45 secondes — répond à votre place' : '~45 seconds — fills the calculator for you'}</span>
            </span>
          </span>
          <span className="ml-3 text-lg transition-transform duration-200 group-hover:translate-x-1" aria-hidden>→</span>
        </button>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        {wizardOpen ? (
          <PaintWizard
            locale={locale}
            copy={{
              doorsTotal: c.doorsTotal,
              windowsTotal: c.windowsTotal,
              coverageTip: c.coverageTip,
              wasteTip: c.wasteTip,
            }}
            i={i}
            set={set}
            setRoom={setRoom}
            addRoom={addRoom}
            removeRoom={removeRoom}
            goal={wizardGoal}
            setGoal={setWizardGoal}
            onClose={() => setWizardOpen(false)}
          />
        ) : wizardGoal === 'reverse' ? (
          /* Reverse mode after the quiz closes: a compact "what you have" card so
             the coverage answer on the right stays adjustable — and a way back to
             the forward "how much paint do I need?" calculator. */
          <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-text">{locale === 'fr' ? 'Ce que vous avez' : 'What you have'}</h2>
              <button type="button" onClick={() => setWizardGoal('forward')} className="text-xs font-medium text-brand-text hover:underline">
                {locale === 'fr' ? '↔ Calculer la peinture requise' : '↔ Switch to: paint needed'}
              </button>
            </div>
            <div className="space-y-4">
              <Segmented label={c.showPaintIn} value={i.outputUnit} onChange={(v) => set({ outputUnit: v })} options={[{ v: 'gallon', l: c.gallons }, { v: 'litre', l: c.litres }]} />
              <div className="rounded-md border border-border p-3">
                <Field
                  label={(locale === 'fr' ? 'Peinture que vous avez' : 'Paint you have') + ` (${i.outputUnit === 'gallon' ? 'gal' : 'L'})`}
                  value={i.gallonsHave}
                  onChange={(n) => set({ gallonsHave: Math.max(0, n) })}
                  min={0}
                  step={0.25}
                  suffix={i.outputUnit === 'gallon' ? 'gal' : 'L'}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label={tr('calc.coats')} value={i.coats} onChange={(n) => set({ coats: Math.max(1, Math.round(n)) })} min={1} />
                <div>
                  <span className="mb-1 flex items-center text-sm font-medium text-text-body">
                    {c.coverageLabel}
                    <InfoTip text={c.coverageTip} />
                  </span>
                  <Field label="" value={i.coveragePerGallon} onChange={(n) => set({ coveragePerGallon: Math.max(50, n) })} min={50} step={10} suffix={c.sqft} />
                </div>
              </div>
            </div>
          </form>
        ) : (
        /* Inputs */
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
          <div className="mb-5 flex flex-wrap gap-6">
            <Segmented label={c.showPaintIn} value={i.outputUnit} onChange={(v) => set({ outputUnit: v })} options={[{ v: 'gallon', l: c.gallons }, { v: 'litre', l: c.litres }]} />
            <Segmented
              label={tr('calc.input')}
              value={i.inputMode}
              onChange={(v) => {
                if (v === 'area' && i.inputMode === 'dimensions') {
                  const inUserUnit = i.unit === 'metric' ? r.paintableArea * 0.09290304 : r.paintableArea
                  set({ inputMode: v, totalArea: Math.round(inUserUnit * 10) / 10 })
                } else {
                  set({ inputMode: v })
                }
              }}
              options={[{ v: 'dimensions', l: tr('calc.dimensions') }, { v: 'area', l: tr('calc.totalArea') }]}
            />
          </div>

          {i.inputMode === 'dimensions' ? (
            <>
              <div className="space-y-3">
                {i.rooms.map((room, idx) => {
                  const isCustom = dropdownValue(room.name) === CUSTOM_ROOM_KEY
                  return (
                  <div key={idx} className="rounded-md border border-border p-3">
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <select
                        value={dropdownValue(room.name)}
                        onChange={(e) => applyPreset(idx, e.target.value)}
                        aria-label={tr('calc.roomType')}
                        className={`rounded-md border border-border bg-surface-2 px-2 py-1.5 text-sm text-text-body focus:border-brand focus:outline-none ${isCustom ? '' : 'w-full sm:w-1/2'}`}
                      >
                        <option value="" disabled>{tr('calc.selectRoom')}</option>
                        {ROOM_PRESETS.map((p) => (
                          <option key={p.label} value={p.label}>{presetLabel(p, locale)}</option>
                        ))}
                        <option value={CUSTOM_ROOM_KEY}>{tr('calc.customRoom')}</option>
                      </select>
                      {isCustom && (
                        <input
                          type="text"
                          value={room.name ?? ''}
                          onChange={(e) => setRoom(idx, { name: e.target.value })}
                          placeholder={tr('calc.roomName')}
                          className="min-w-0 flex-1 rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text outline-none focus:border-brand"
                        />
                      )}
                      {i.rooms.length > 1 && (
                        <button type="button" onClick={() => removeRoom(idx)} className="text-xs text-danger hover:underline whitespace-nowrap">
                          {tr('calc.remove')}
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <SmartField label={tr('calc.length')} imperialValue={room.length} onChangeImperial={(n) => setRoom(idx, { length: n })} imperialUnit="ft" />
                      <SmartField label={tr('calc.width')} imperialValue={room.width} onChangeImperial={(n) => setRoom(idx, { width: n })} imperialUnit="ft" />
                      <SmartField label={tr('calc.height')} imperialValue={room.height} onChangeImperial={(n) => setRoom(idx, { height: n })} imperialUnit="ft" />
                    </div>
                  </div>
                  )
                })}
                <button type="button" onClick={addRoom} className="w-full rounded-md border border-dashed border-border py-2 text-sm font-medium text-brand-text hover:bg-surface-2">
                  {tr('calc.addRoom')}
                </button>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <Field label={c.doorsTotal} value={i.doors} onChange={(n) => set({ doors: n })} />
                <Field label={c.windowsTotal} value={i.windows} onChange={(n) => set({ windows: n })} />
                <Field label={tr('calc.coats')} value={i.coats} onChange={(n) => set({ coats: n })} min={1} />
              </div>

              <label className="mt-4 flex items-center gap-2 text-sm text-text-body">
                <input type="checkbox" checked={i.includeCeiling} onChange={(e) => set({ includeCeiling: e.target.checked })} />
                {c.alsoCeilings}
              </label>
            </>
          ) : (
            <>
              <div className="rounded-md border border-border p-3">
                <SmartField
                  label={tr('calc.totalArea')}
                  imperialValue={i.totalArea}
                  onChangeImperial={(n) => set({ totalArea: n })}
                  imperialUnit="sq ft"
                />
                <p className="mt-2 text-xs text-text-subtle">{tr('calc.areaHint')}</p>
              </div>
              <div className="mt-4">
                <Field label={tr('calc.coats')} value={i.coats} onChange={(n) => set({ coats: n })} min={1} />
              </div>
            </>
          )}
          <label className="mt-2 flex items-center gap-2 text-sm text-text-body">
            <input type="checkbox" checked={i.needPrimer} onChange={(e) => set({ needPrimer: e.target.checked })} />
            {c.includePrimer}
          </label>

          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center font-medium text-text-body">
                {tr('calc.wasteAllowance')}
                <InfoTip text={c.wasteTip} />
              </span>
              <span className="tnum text-brand-text">{i.wastePct}%</span>
            </div>
            <input type="range" min={0} max={25} step={1} value={i.wastePct} onChange={(e) => set({ wastePct: parseInt(e.target.value) })} className="mt-1 w-full accent-brand" />
            <div className="mt-2">
              <span className="mb-1 flex items-center text-sm font-medium text-text-body">
                {c.coverageLabel}
                <InfoTip text={c.coverageTip} />
              </span>
              <span className="flex items-center gap-1 rounded-md border border-border bg-surface px-3 focus-within:border-brand">
                <input type="number" inputMode="decimal" value={i.coveragePerGallon} min={50} step={10} onChange={(e) => set({ coveragePerGallon: parseFloat(e.target.value) || 0 })} className="tnum h-11 w-full bg-transparent text-md text-text outline-none" />
                <span className="text-sm text-text-subtle">{c.sqft}</span>
              </span>
            </div>
            <p className="mt-2 text-xs text-text-subtle">{c.assumptionsNote}</p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>
        )}

        {/* Result column — shows the coverage answer whenever the user is in
            reverse mode (during the quiz AND after it closes), otherwise the
            normal forward buy-list sheet. */}
        <div className="lg:col-span-2">
          {wizardGoal === 'reverse' ? (
            <section className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm" aria-live="polite">
              <div className="border-b border-border bg-brand-tint/60 p-5">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  {locale === 'fr' ? 'Votre couverture' : 'Your coverage'}
                </div>
                <div className="tnum mt-1 text-result font-bold text-text">
                  {i.unit === 'metric' ? rev.coverageM2 : rev.coverageSqft}{' '}
                  <span className="text-2xl font-semibold">{i.unit === 'metric' ? 'm²' : (locale === 'fr' ? 'pi²' : 'sq ft')}</span>
                </div>
                {/* Even base-size, dark text — matches the forward result card's
                    lines under the headline number (no size/colour gradient). */}
                <div className="mt-2 space-y-1 text-base text-text">
                  <p>
                    {locale === 'fr'
                      ? `de surface peinturable en ${i.coats} couche${i.coats === 1 ? '' : 's'}`
                      : `of paintable surface in ${i.coats} coat${i.coats === 1 ? '' : 's'}`}
                  </p>
                  <p>
                    {locale === 'fr'
                      ? `Environ un mur de ${i.unit === 'metric' ? rev.equivSquareM : rev.equivSquareFt} × ${i.unit === 'metric' ? rev.equivSquareM : rev.equivSquareFt} ${i.unit === 'metric' ? 'm' : 'pi'}.`
                      : `Roughly a wall of ${rev.equivSquareFt} × ${rev.equivSquareFt} ft (${rev.equivSquareM} × ${rev.equivSquareM} m).`}
                  </p>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-sm font-semibold text-text">
                  {locale === 'fr' ? 'Détail' : 'Breakdown'}
                </h3>
                <ul className="mt-2 space-y-1 text-sm text-text-body">
                  <li>
                    <span className="text-text-muted">{locale === 'fr' ? 'Peinture' : 'Paint'}: </span>
                    <span className="font-medium">{rev.totalGallons} gal ({rev.totalLitres} L)</span>
                  </li>
                  <li>
                    <span className="text-text-muted">{locale === 'fr' ? 'Couches' : 'Coats'}: </span>
                    <span className="font-medium">{i.coats}</span>
                  </li>
                  <li>
                    <span className="text-text-muted">{locale === 'fr' ? 'Couverture' : 'Coverage rate'}: </span>
                    <span className="font-medium">{i.coveragePerGallon} sq ft/gal</span>
                  </li>
                </ul>
                <p className="mt-3 text-xs text-text-subtle">
                  {locale === 'fr'
                    ? 'Estimations seulement — la couverture réelle varie selon la surface et la peinture.'
                    : 'Estimates only — actual coverage varies with surface and paint type.'}
                </p>
              </div>
            </section>
          ) : (() => {
            // Translate the math volume into a real-world buy list — the exact
            // mix of can sizes to grab at the store, shown in the headline labels.
            const paintCans = planPaintCans(r.paint.qty, r.paint.unit)
            const primerCans = r.primer.qty > 0 ? planPaintCans(r.primer.qty, r.primer.unit) : []
            // Compact one-line phrase: "2 × 1 gal + 1 × 1 qt cans" — × notation with
            // the container word said ONCE at the end so the whole mix fits a line.
            const cansPhrase = (plan: PaintCanPart[]): string => {
              if (plan.length === 0) return ''
              const isPail = plan.some((p) => p.sizeLabel.includes('pail'))
              const total = plan.reduce((s, p) => s + p.count, 0)
              const sizeOf = (s: string) =>
                locale === 'fr'
                  ? s.replace('-gal pail', ' gal').replace('18.9 L pail', '18,9 L').replace(' pail', '')
                  : s.replace('-gal pail', '-gal').replace(' pail', '')
              const body = plan.map((p) => `${p.count} × ${sizeOf(p.sizeLabel)}`).join(' + ')
              if (locale === 'fr') {
                const noun = isPail ? (total === 1 ? 'seau' : 'seaux') : (total === 1 ? 'contenant' : 'contenants')
                return `${body} ${noun}`
              }
              return `${body} ${isPail ? 'pail' : 'can'}${total === 1 ? '' : 's'}`
            }
            const paintLabel = paintCans.length > 0
              ? (locale === 'fr'
                  ? `Achetez ${cansPhrase(paintCans)} ${c.primaryLabel}`
                  : `Buy ${cansPhrase(paintCans)} ${c.primaryLabel}`)
              : c.primaryLabel
            const primerLabelSuffix = primerCans.length > 0 ? ` — ${cansPhrase(primerCans)}` : ''
            return (
            <ResultSheet
              primary={{ name: c.primaryName, label: paintLabel, qty: r.paint.qty, unit: r.paint.unit, query: 'interior wall paint' }}
              secondary={r.primer.qty > 0 ? { name: c.secondaryName, label: c.secondaryName + primerLabelSuffix, qty: r.primer.qty, unit: r.primer.unit, query: 'paint primer' } : null}
              lines={r.lines}
              assumptions={assumptions}
              explain={buildExplain()}
            />
            )
          })()}
        </div>
      </div>

      {/* Supporting content */}
      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{c.aboutH2}</h2>
        <p className="mt-3">{c.aboutP1}</p>
        <h2 className="mt-8 text-2xl">{c.howH2}</h2>
        <p className="mt-3">{c.howP1}</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{formulaText}</pre>
        <p className="mt-3">{howP2Text}</p>
        <h3 className="mt-6 text-xl">{c.mistakesH3}</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {c.mistakes.map((m, k) => <li key={k}>{m}</li>)}
        </ul>
      </article>

      <Faq items={c.faqs} />

      <section className="mt-10">
        <h2 className="text-2xl">{c.relatedH2}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">{c.reviewer}</div>
    </div>
  )
}
